import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Autocomplete,
  Alert,
} from "@mui/material";
import contractItemsApi from "../../services/contractItems.service";
import { formatCurrency } from "../../../../utils/formatters";

const ContractItemModal = ({
  open,
  onClose,
  onSuccess,
  contractId,
  editMode = false,
  itemData = null,
  parentItem = null,
}) => {
  const [formData, setFormData] = useState({
    itemName: "",
    description: "",
    unit: "",
    contractQuantity: "",
    unitPrice: "",
    contractAmount: "", // Thêm contractAmount vào state
    startDate: "",
    endDate: "",
    status: "Chưa bắt đầu",
    priority: 2,
    completionPercentage: 0,
    difficultyLevel: 1,
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [generatedCode, setGeneratedCode] = useState("");
  const [errors, setErrors] = useState({});

  // Hàm format số theo chuẩn Việt Nam (dấu "." phân tách hàng nghìn, dấu "," phân tách thập phân)
  const formatNumber = (value) => {
    if (!value) return "";

    // Xóa tất cả ký tự không phải số, dấu "," và "."
    const cleanValue = value.toString().replace(/[^\d,.]/g, "");

    // Tách phần nguyên và phần thập phân
    const parts = cleanValue.split(",");
    const integerPart = parts[0].replace(/\./g, ""); // Xóa dấu "." cũ
    const decimalPart = parts[1] ? "," + parts[1] : "";

    // Format phần nguyên với dấu "." phân tách hàng nghìn
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");

    return formattedInteger + decimalPart;
  };

  // Hàm unformat số (chuyển về dạng số thuần túy)
  const unformatNumber = (value) => {
    if (!value) return "";
    return value.toString().replace(/\./g, "").replace(",", ".");
  };

  // Hàm xử lý thay đổi cho trường unitPrice
  const handleUnitPriceChange = (value) => {
    const formattedValue = formatNumber(value);

    setFormData((prev) => {
      const newFormData = { ...prev, unitPrice: formattedValue };

      // Auto-calculate contract amount when quantity or price changes
      const quantity = Number(prev.contractQuantity) || 0;
      const price = Number(unformatNumber(formattedValue)) || 0;
      newFormData.contractAmount = quantity && price ? quantity * price : 0;

      return newFormData;
    });

    // Clear error when user starts typing
    if (errors.unitPrice) {
      setErrors((prev) => ({ ...prev, unitPrice: "" }));
    }
  };

  // Predefined options
  const statusOptions = [
    "Chưa bắt đầu",
    "Đang thực hiện",
    "Hoàn thành",
    "Tạm dừng",
    "Hủy bỏ",
  ];

  const priorityOptions = [
    { value: 1, label: "Cao" },
    { value: 2, label: "Trung bình" },
    { value: 3, label: "Thấp" },
  ];

  const difficultyOptions = [
    { value: 1, label: "1" },
    { value: 2, label: "2" },
    { value: 3, label: "3" },
    { value: 4, label: "4" },
    { value: 5, label: "5" },
  ];

  const unitOptions = [
    "%",
    "Bộ",
    "Cái",
    "Điểm",
    "Gói",
    "ha",
    "kg",
    "m",
    "m²",
    "Mảnh QĐ",
    "Ngày",
    "Phần",
    "Tấn",
    "Tháng",
  ];

  // Load form data when editing
  useEffect(() => {
    if (editMode && itemData) {
      setFormData({
        itemName: itemData.itemName || "",
        description: itemData.description || "",
        unit: itemData.unit || "",
        contractQuantity: itemData.contractQuantity || "",
        unitPrice: formatNumber(itemData.unitPrice || ""),
        contractAmount: itemData.contractAmount || "", // Thêm contractAmount
        startDate: itemData.startDate
          ? new Date(itemData.startDate).toISOString().split("T")[0]
          : "",
        endDate: itemData.endDate
          ? new Date(itemData.endDate).toISOString().split("T")[0]
          : "",
        status: itemData.status || "Chưa bắt đầu",
        priority: itemData.priority || 2,
        completionPercentage: itemData.completionPercentage || 0,
        difficultyLevel: itemData.difficultyLevel || 1,
        notes: itemData.notes || "",
      });
    } else {
      // Reset form for new item
      setFormData({
        itemName: "",
        description: "",
        unit: "",
        contractQuantity: "",
        unitPrice: "",
        contractAmount: "", // Thêm contractAmount
        startDate: "",
        endDate: "",
        status: "Chưa bắt đầu",
        priority: 2,
        completionPercentage: 0,
        difficultyLevel: 1,
        notes: "",
      });
    }
  }, [editMode, itemData]);

  // Generate item code when adding new item
  useEffect(() => {
    const generateItemCode = async () => {
      try {
        const response = await contractItemsApi.generateItemCode(
          contractId,
          parentItem?.itemId
        );
        if (response.Success) {
          setGeneratedCode(response.ItemCode);
        }
      } catch (error) {
        console.error("Error generating item code:", error);
      }
    };

    if (open && !editMode && contractId) {
      generateItemCode();
    }
  }, [open, editMode, contractId, parentItem]);

  const handleInputChange = (field, value) => {
    // Prevent editing completion percentage as it's auto-calculated from payments
    if (field === "completionPercentage") {
      console.warn(
        "CompletionPercentage is read-only and calculated from payments"
      );
      return;
    }

    // Xử lý riêng cho unitPrice để format số
    if (field === "unitPrice") {
      handleUnitPriceChange(value);
      return;
    }

    setFormData((prev) => {
      const newFormData = { ...prev, [field]: value };

      // Auto-calculate contract amount when quantity or price changes
      if (field === "contractQuantity") {
        const quantity = Number(value) || 0;
        const price = Number(unformatNumber(prev.unitPrice)) || 0;
        newFormData.contractAmount = quantity && price ? quantity * price : 0;
      }

      return newFormData;
    });

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Bỏ validation bắt buộc cho itemName vì đã cho phép null trong database
    // if (!formData.itemName.trim()) {
    //   newErrors.itemName = "Hạng mục công việc là bắt buộc";
    // }

    // Validate dates
    if (formData.startDate && formData.endDate) {
      if (new Date(formData.startDate) > new Date(formData.endDate)) {
        newErrors.endDate = "Ngày kết thúc phải sau ngày bắt đầu";
      }
    }

    // Validate numbers
    if (
      formData.contractQuantity &&
      isNaN(parseFloat(formData.contractQuantity))
    ) {
      newErrors.contractQuantity = "Khối lượng phải là số hợp lệ";
    }

    if (formData.unitPrice) {
      const unformattedPrice = unformatNumber(formData.unitPrice);
      if (
        isNaN(parseFloat(unformattedPrice)) ||
        parseFloat(unformattedPrice) < 0
      ) {
        newErrors.unitPrice = "Đơn giá phải là số dương hợp lệ";
      }
    }

    // Validate completion percentage - Removed because it's now auto-calculated from payments
    // Completion percentage is read-only and managed by payment system

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      // Debug logging để kiểm tra độ chính xác

      // Tính toán contractAmount từ contractQuantity và unitPrice - Sử dụng Number() thay vì parseFloat()
      const quantity = formData.contractQuantity
        ? Number(formData.contractQuantity)
        : null;
      const price = formData.unitPrice
        ? Number(unformatNumber(formData.unitPrice))
        : null;
      const contractAmount = quantity && price ? quantity * price : null;

      const submitData = {
        ...formData,
        contractId: parseInt(contractId),
        parentItemId: parentItem?.itemId || null,
        contractQuantity: quantity,
        unitPrice: price,
        contractAmount: contractAmount, // Thêm contractAmount được tính toán
        // completionPercentage: removed - now auto-calculated from payments
        difficultyLevel: formData.difficultyLevel
          ? parseInt(formData.difficultyLevel)
          : 1,
        startDate: formData.startDate || null,
        endDate: formData.endDate || null,
      };

      let response;
      if (editMode) {
        response = await contractItemsApi.updateItem(
          itemData.itemId,
          submitData
        );
      } else {
        response = await contractItemsApi.createItemWithAutoCode(submitData);
      }

      if (response.success || response.Success) {
        onSuccess?.(
          editMode ? "Cập nhật hạng mục thành công" : "Tạo hạng mục thành công"
        );
        handleClose();
      } else {
        setErrors({
          submit: response.message || response.Message || "Có lỗi xảy ra",
        });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setErrors({ submit: "Lỗi kết nối đến máy chủ" });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      itemName: "",
      description: "",
      unit: "",
      contractQuantity: "",
      unitPrice: "",
      contractAmount: "", // Thêm contractAmount
      startDate: "",
      endDate: "",
      status: "Chưa bắt đầu",
      priority: 2,
      completionPercentage: 0,
      difficultyLevel: 1,
      notes: "",
    });
    setErrors({});
    setGeneratedCode("");
    onClose();
  };

  const calculatePreviewAmount = () => {
    const quantity = parseFloat(formData.contractQuantity) || 0;
    const price = parseFloat(unformatNumber(formData.unitPrice)) || 0;
    return quantity * price;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          border: "1px solid #e0e0e0",
          boxShadow: "none",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontFamily: "Inter, sans-serif",
          fontSize: "1.25rem",
          fontWeight: 600,
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        {editMode ? "Chỉnh sửa hạng mục" : "Thêm hạng mục mới"}
        {parentItem && (
          <Typography
            variant="body2"
            sx={{
              fontFamily: "Inter, sans-serif",
              fontSize: "0.875rem",
              color: "text.secondary",
              mt: 0.5,
            }}
          >
            Hạng mục con của: {parentItem.itemCode} - {parentItem.itemName}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        {errors.submit && (
          <Alert
            severity="error"
            sx={{ mb: 2, fontFamily: "Inter, sans-serif" }}
          >
            {errors.submit}
          </Alert>
        )}

        {/* STT (chỉ hiển thị khi thêm mới) */}
        {!editMode && generatedCode && (
          <Box
            mb={2}
            p={2}
            sx={{ backgroundColor: "#f5f5f5", borderRadius: 1 }}
          >
            <Typography
              variant="body2"
              sx={{
                fontFamily: "Inter, sans-serif",
                fontSize: "0.875rem",
                fontWeight: 500,
              }}
            >
              STT sẽ được tạo: <strong>{generatedCode}</strong>
            </Typography>
          </Box>
        )}

        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {/* Hạng mục công việc - 12/12 */}
          <Grid size={12}>
            <TextField
              fullWidth
              label="Hạng mục công việc"
              value={formData.itemName}
              onChange={(e) => handleInputChange("itemName", e.target.value)}
              error={!!errors.itemName}
              helperText={errors.itemName}
              sx={{
                "& .MuiInputLabel-root": {
                  fontFamily: "Inter, sans-serif",
                  fontSize: "0.875rem",
                  transform: "translate(14px, 16px) scale(1)",
                  "&.Mui-focused, &.MuiFormLabel-filled": {
                    transform: "translate(14px, -9px) scale(0.75)",
                  },
                },
                "& .MuiInputBase-input": {
                  fontFamily: "Inter, sans-serif",
                  fontSize: "0.875rem",
                },
              }}
            />
          </Grid>

          {/* ĐVT - 6/12 */}
          <Grid size={6}>
            <Autocomplete
              freeSolo
              options={unitOptions}
              value={formData.unit}
              onChange={(event, newValue) =>
                handleInputChange("unit", newValue)
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="ĐVT"
                  sx={{
                    "& .MuiInputLabel-root, & .MuiInputBase-input": {
                      fontFamily: "Inter, sans-serif",
                      fontSize: "0.875rem",
                    },
                  }}
                />
              )}
            />
          </Grid>

          {/* Khối lượng và Đơn giá - 6/12 mỗi cái */}
          <Grid size={6}>
            <TextField
              fullWidth
              label="Khối lượng"
              type="number"
              value={formData.contractQuantity}
              onChange={(e) =>
                handleInputChange("contractQuantity", e.target.value)
              }
              error={!!errors.contractQuantity}
              helperText={errors.contractQuantity}
              inputProps={{ min: 0, step: 0.0001 }}
              sx={{
                "& .MuiInputLabel-root, & .MuiInputBase-input": {
                  fontFamily: "Inter, sans-serif",
                  fontSize: "0.875rem",
                },
              }}
            />
          </Grid>

          <Grid size={6}>
            <TextField
              fullWidth
              label="Đơn giá (VND)"
              value={formData.unitPrice}
              onChange={(e) => handleInputChange("unitPrice", e.target.value)}
              error={!!errors.unitPrice}
              helperText={errors.unitPrice}
              placeholder="Nhập đơn giá (VD: 1.000.000,50)"
              sx={{
                "& .MuiInputLabel-root, & .MuiInputBase-input": {
                  fontFamily: "Inter, sans-serif",
                  fontSize: "0.875rem",
                },
              }}
            />
          </Grid>

          {/* Thành tiền preview */}
          {formData.contractQuantity && formData.unitPrice && (
            <Grid size={12}>
              <Box p={2} sx={{ backgroundColor: "#e3f2fd", borderRadius: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: "Inter, sans-serif",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                  }}
                >
                  Thành tiền dự kiến:{" "}
                  {formatCurrency(calculatePreviewAmount()).replace(
                    /\s*₫\s*$/,
                    ""
                  )}
                </Typography>
              </Box>
            </Grid>
          )}

          {/* Ngày bắt đầu và kết thúc - 6/12 mỗi cái */}
          <Grid size={6}>
            <TextField
              fullWidth
              label="Ngày bắt đầu"
              type="date"
              value={formData.startDate}
              onChange={(e) => handleInputChange("startDate", e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{
                "& .MuiInputLabel-root, & .MuiInputBase-input": {
                  fontFamily: "Inter, sans-serif",
                  fontSize: "0.875rem",
                },
              }}
            />
          </Grid>

          <Grid size={6}>
            <TextField
              fullWidth
              label="Ngày kết thúc"
              type="date"
              value={formData.endDate}
              onChange={(e) => handleInputChange("endDate", e.target.value)}
              error={!!errors.endDate}
              helperText={errors.endDate}
              InputLabelProps={{ shrink: true }}
              sx={{
                "& .MuiInputLabel-root, & .MuiInputBase-input": {
                  fontFamily: "Inter, sans-serif",
                  fontSize: "0.875rem",
                },
              }}
            />
          </Grid>

          {/* Trạng thái và Độ ưu tiên - 6/12 mỗi cái */}
          <Grid size={6}>
            <FormControl fullWidth>
              <InputLabel
                sx={{ fontFamily: "Inter, sans-serif", fontSize: "0.875rem" }}
              >
                Trạng thái
              </InputLabel>
              <Select
                value={formData.status}
                onChange={(e) => handleInputChange("status", e.target.value)}
                label="Trạng thái"
                sx={{
                  "& .MuiSelect-select": {
                    fontFamily: "Inter, sans-serif",
                    fontSize: "0.875rem",
                  },
                }}
              >
                {statusOptions.map((status) => (
                  <MenuItem
                    key={status}
                    value={status}
                    sx={{
                      fontFamily: "Inter, sans-serif",
                      fontSize: "0.875rem",
                    }}
                  >
                    {status}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={6}>
            <FormControl fullWidth>
              <InputLabel
                sx={{ fontFamily: "Inter, sans-serif", fontSize: "0.875rem" }}
              >
                Độ ưu tiên
              </InputLabel>
              <Select
                value={formData.priority}
                onChange={(e) => handleInputChange("priority", e.target.value)}
                label="Độ ưu tiên"
                sx={{
                  "& .MuiSelect-select": {
                    fontFamily: "Inter, sans-serif",
                    fontSize: "0.875rem",
                  },
                }}
              >
                {priorityOptions.map((option) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    sx={{
                      fontFamily: "Inter, sans-serif",
                      fontSize: "0.875rem",
                    }}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={6}>
            <FormControl fullWidth>
              <InputLabel
                sx={{ fontFamily: "Inter, sans-serif", fontSize: "0.875rem" }}
              >
                Khó khăn
              </InputLabel>
              <Select
                value={formData.difficultyLevel}
                onChange={(e) =>
                  handleInputChange("difficultyLevel", e.target.value)
                }
                label="Khó khăn"
                sx={{
                  "& .MuiSelect-select": {
                    fontFamily: "Inter, sans-serif",
                    fontSize: "0.875rem",
                  },
                }}
              >
                {difficultyOptions.map((option) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    sx={{
                      fontFamily: "Inter, sans-serif",
                      fontSize: "0.875rem",
                    }}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Tiến độ - 6/12 (đã bỏ Mô tả để tiết kiệm không gian) */}
          <Grid size={6}>
            <TextField
              fullWidth
              label="Tiến độ (%)"
              type="number"
              value={formData.completionPercentage}
              InputProps={{
                readOnly: true,
              }}
              helperText="Tiến độ được tự động tính từ khối lượng thanh toán"
              inputProps={{ min: 0, max: 100, step: 0.1 }}
              sx={{
                "& .MuiInputLabel-root, & .MuiInputBase-input": {
                  fontFamily: "Inter, sans-serif",
                  fontSize: "0.875rem",
                },
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#f8f9fa",
                  "& fieldset": { borderColor: "#e0e0e0" },
                  "&:hover fieldset": { borderColor: "#b0b0b0" },
                },
              }}
            />
          </Grid>

          {/* TODO: Tạm ẩn ô nhập liệu Mô tả - sẽ mở lại khi cần */}
          {/* <Grid size={9}>
            <TextField
              fullWidth
              label="Mô tả"
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              sx={{
                "& .MuiInputLabel-root, & .MuiInputBase-input": {
                  fontFamily: "Inter, sans-serif",
                  fontSize: "0.875rem",
                },
              }}
            />
          </Grid> */}

          {/* TODO: Tạm ẩn ô nhập liệu Ghi chú - sẽ mở lại khi cần */}
          {/* Ghi chú - 12/12 */}
          {/* <Grid size={12}>
            <TextField
              fullWidth
              label="Ghi chú"
              multiline
              rows={2}
              value={formData.notes}
              onChange={(e) => handleInputChange("notes", e.target.value)}
              sx={{
                "& .MuiInputLabel-root, & .MuiInputBase-input": {
                  fontFamily: "Inter, sans-serif",
                  fontSize: "0.875rem",
                },
              }}
            />
          </Grid> */}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3, borderTop: "1px solid #e0e0e0" }}>
        <Button
          onClick={handleClose}
          sx={{
            textTransform: "none",
            fontFamily: "Inter, sans-serif",
            color: "text.secondary",
          }}
        >
          Hủy
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading}
          variant="contained"
          sx={{
            textTransform: "none",
            fontFamily: "Inter, sans-serif",
            boxShadow: "none",
            "&:hover": { boxShadow: "none" },
          }}
        >
          {loading ? "Đang xử lý..." : editMode ? "Cập nhật" : "Tạo hạng mục"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContractItemModal;
